import React, { useMemo } from "react";
import { AreaChart, ChartReferenceLineProps } from "@mantine/charts";
import { cumulativeStdNormalProbability as stdNormCdf } from "simple-statistics";

function gaussianPdf(x: number, mean: number, sigma: number) {
  const coeff = 1 / (sigma * Math.sqrt(2 * Math.PI));
  const expo = -((x - mean) ** 2) / (2 * sigma * sigma);
  return coeff * Math.exp(expo);
}

export interface NormalDistChartSectionedProps {
  referenceLines?: ChartReferenceLineProps[];
  width?: number;
  h?: number;
}

const NormalDistChartSectioned: React.FC<NormalDistChartSectionedProps> = ({
  referenceLines,
  width,
  h,
}) => {
  const mean = 0;
  const stdDev = 1;
  const rangeStd = 3;
  const points = 121;
  const height = h || 220;

  const data = useMemo(() => {
    type Row = {
      z: number;
      P0_10: number | null;
      P11_25: number | null;
      P26_75: number | null;
      P76_90: number | null;
      P91_100: number | null;
    };
    const rows: Row[] = [];
    const startZ = -rangeStd;
    const endZ = rangeStd;
    const stepZ = (endZ - startZ) / (points - 1);
    for (let i = 0; i < points; i += 1) {
      const z = startZ + i * stepZ;
      const x = mean + z * stdDev;
      const y = gaussianPdf(x, mean, stdDev);
      const p = stdNormCdf(z) * 100;

      const row: Row = {
        z: +z.toFixed(4),
        P0_10: null,
        P11_25: null,
        P26_75: null,
        P76_90: null,
        P91_100: null,
      };

      if (p <= 10) row.P0_10 = y;
      else if (p <= 25) row.P11_25 = y;
      else if (p <= 75) row.P26_75 = y;
      else if (p <= 90) row.P76_90 = y;
      else row.P91_100 = y;

      rows.push(row);
    }
    return rows;
  }, [mean, stdDev, rangeStd, points]);

  const tickFormatter = (z: number) => {
    switch (z) {
      case -2:
        return "0-10%";
      case -1:
        return "11-25%";
      case 0:
        return "26-75%";
      case 1:
        return "76-90%";
      case 2:
        return "91-100%";
    }
  };

  const zFromPercentile = (percentile: number) => {
    const target = percentile / 100;
    let lo = -rangeStd;
    let hi = rangeStd;
    let mid = 0;
    for (let i = 0; i < 60; i += 1) {
      mid = (lo + hi) / 2;
      const c = stdNormCdf(mid);
      if (c > target) hi = mid;
      else lo = mid;
    }
    return +mid.toFixed(1);
  };

  return (
    <AreaChart
      h={height}
      data={data}
      dataKey="z"
      w={width || null}
      series={[
        { name: "P0_10", color: "red.6" },
        { name: "P11_25", color: "orange.6" },
        { name: "P26_75", color: "blue.6" },
        { name: "P76_90", color: "teal.6" },
        { name: "P91_100", color: "green.6" },
      ]}
      curveType="natural"
      withDots={false}
      withLegend={false}
      withTooltip={false}
      valueFormatter={(v) => (typeof v === "number" ? v.toFixed(4) : String(v))}
      yAxisLabel="Probability density"
      withYAxis={false}
      xAxisLabel="Percentile"
      xAxisProps={{
        ticks: [-2, -1, 0, 1, 2],
        tickFormatter,
      }}
      referenceLines={referenceLines.map((line) => ({
        ...line,
        x: zFromPercentile(line.x as number),
      }))}
      styles={{
        referenceLine: {
          line: {
            strokeWidth: 3,
          },
        },
      }}
    />
  );
};

export default NormalDistChartSectioned;
